/**
 * CENTRE D'ACTIVITÉ — LA PROJECTION, SANS LE DOM.
 *
 * Gestion disait deux fois « quelque chose mérite ton attention » : une page
 * Alertes, une page Notifications. Ce module fusionne les deux EXPÉRIENCES —
 * pas les deux modèles métier, qui restent distincts et le doivent :
 *
 *   ALERTE       un état opérationnel, potentiellement encore vrai. Elle a un
 *                cycle de vie (active → prise en charge → résolue) et des
 *                actions propres.
 *   NOTIFICATION un message adressé à quelqu'un, à un instant donné. Elle est
 *                lue ou non lue, et ne « se résout » pas.
 *
 * Confondre les deux aurait détruit une distinction utile pour gagner un
 * écran. On projette donc, on ne fusionne pas.
 *
 * Aucune requête ici : le module reçoit ce que les deux API existantes
 * renvoient déjà, avec leurs propres contrôles de permission. Un utilisateur
 * qui n'a pas le droit de lire les alertes reçoit une liste vide et garde un
 * Centre parfaitement fonctionnel pour ses notifications.
 */

export interface AlerteSource {
  service_key: string
  service_name: string
  external_alert_id: string
  severity: string
  title: string
  state: string
  origin: 'SERVICE' | 'RULE'
  instance_id: string | null
  first_seen_at: string
  last_seen_at: string
  acknowledged_at: string | null
  acknowledged_by_name: string | null
  resolved_at: string | null
  url: string | null
  rule_name: string | null
  duration: string | null
  /** Page DANS Gestion (incident de règle, alerte de Sonde sous /infrastructure). */
  deep_link?: string | null
  /** Sonde : « e-mail déjà envoyé par Sonde », « acquittée dans Gestion ». */
  note?: string | null
}

export interface NotificationSource {
  id: string
  severity: string
  title: string
  body: string
  deep_link: string | null
  alert_state: string | null
  created_at: string
  read_at: string | null
  /** D'où vient la notification (08/10/2026) : la famille et le module du type. */
  type_key?: string | null
  family_id?: string | null
  family_label?: string | null
  module_label?: string | null
}

/** Un point d'attention ÉPINGLÉ : une alerte encore vraie. */
export interface PinnedItem {
  key: string
  severity: string
  title: string
  source: string
  since: string
  acknowledged: boolean
  acknowledgedBy: string | null
  /** Une précision à écrire sur la ligne (alertes de Sonde), sinon null. */
  note: string | null
  alert: AlerteSource
}

/** Une ligne du flux chronologique. */
export interface FeedItem {
  key: string
  at: string
  severity: string
  title: string
  detail: string
  source: string
  /** `null` pour un événement d'alerte sans destination propre. */
  deepLink: string | null
  unread: boolean
  /** Ce qui a produit la ligne — sert au vocabulaire, pas au tri. */
  kind: 'NOTIFICATION' | 'ALERT_RESOLVED'
  notificationId: string | null
  /** La famille de la notification ; `null` pour une alerte résolue sans message. */
  family: string | null
  familyLabel: string | null
}

/**
 * RANG DE CRITICITÉ — trois niveaux, pas douze (§17).
 * Plus le rang est bas, plus l'élément remonte.
 */
export function severityRank(s: string): number {
  if (s === 'CRITICAL' || s === 'ERROR') return 0
  if (s === 'ACTION_REQUIRED') return 1
  return 2
}

export function severityLabel(s: string): string {
  return {
    CRITICAL: 'Critique', ERROR: 'Erreur', ACTION_REQUIRED: 'À traiter',
    WARNING: 'Attention', INFO: 'Information', SUCCESS: 'OK', RECOVERY: 'Rétabli',
  }[s] ?? s
}

/**
 * LES ÉPINGLÉES.
 *
 * Une alerte non résolue reste en haut, quel que soit son âge : c'est la
 * décision produit. Elle ne descend pas dans le flux parce qu'elle a trois
 * heures — au contraire, plus elle dure, plus elle compte.
 *
 * Ordre : criticité, puis la PLUS ANCIENNE d'abord. Un problème qui dure
 * depuis six heures passe devant celui qui vient d'apparaître.
 */
export function pinnedAlerts(alerts: AlerteSource[]): PinnedItem[] {
  return alerts
    .filter((a) => a.state !== 'RESOLVED')
    .map((a) => ({
      key: `${a.origin}:${a.service_key}:${a.external_alert_id}`,
      severity: a.severity,
      title: a.title,
      source: a.rule_name && a.origin === 'RULE' ? `${a.service_name} · ${a.rule_name}` : a.service_name,
      since: a.first_seen_at,
      acknowledged: a.acknowledged_at !== null,
      acknowledgedBy: a.acknowledged_by_name,
      note: a.note ?? null,
      alert: a,
    }))
    .sort((x, y) => severityRank(x.severity) - severityRank(y.severity)
      || x.since.localeCompare(y.since))
}

/** Identifiant d'incident porté par un lien profond `/activite/<id>` ou
 *  `/alerts/<id>` — c'est ce qui relie une notification à son alerte. */
export function instanceIdFromLink(link: string | null): string | null {
  if (!link) return null
  const m = /^\/(?:activite|alerts)\/([0-9a-f-]{8,40})$/.exec(link.split('?')[0] ?? '')
  return m ? m[1]! : null
}

/**
 * LE FLUX.
 *
 * Notifications personnelles + alertes qui viennent d'être résolues (§18 :
 * une alerte résolue quitte les épinglées et devient un événement daté).
 *
 * DÉDOUBLONNAGE : une alerte qui a produit une notification n'apparaît pas
 * deux fois. La notification l'emporte — c'est le message réellement reçu,
 * et il porte déjà l'état de l'alerte.
 */
export function activityFeed(
  alerts: AlerteSource[], notifications: NotificationSource[],
): FeedItem[] {
  const liees = new Set(
    notifications.map((n) => instanceIdFromLink(n.deep_link)).filter((x): x is string => x !== null),
  )
  // Une alerte de Sonde est reliée à ses notifications par sa page (/infrastructure/…).
  const pagesNotifiees = new Set(notifications.map((n) => n.deep_link).filter((x): x is string => !!x))

  const depuisNotifs: FeedItem[] = notifications.map((n) => ({
    key: `n:${n.id}`,
    at: n.created_at,
    severity: n.severity,
    title: n.title,
    detail: n.body,
    source: n.alert_state === 'RESOLVED' ? 'Alerte résolue'
      : n.alert_state === 'ACKNOWLEDGED' ? 'Alerte prise en charge'
      : origineDe(n),
    deepLink: n.deep_link,
    unread: n.read_at === null,
    kind: 'NOTIFICATION',
    notificationId: n.id,
    family: n.family_id ?? null,
    familyLabel: n.family_label ?? null,
  }))

  const depuisAlertes: FeedItem[] = alerts
    .filter((a) => a.state === 'RESOLVED' && a.resolved_at !== null)
    .filter((a) => !(a.instance_id && liees.has(a.instance_id)))
    .filter((a) => !(a.service_key === 'sonde' && a.deep_link && pagesNotifiees.has(a.deep_link)))
    .map((a) => ({
      key: `a:${a.origin}:${a.service_key}:${a.external_alert_id}`,
      at: a.resolved_at!,
      severity: 'RECOVERY',
      title: `${a.title} — alerte résolue`,
      detail: a.duration ? `A duré ${a.duration}` : '',
      source: a.service_name,
      deepLink: a.instance_id ? `/activite/${a.instance_id}` : a.service_key === 'sonde' ? lienSonde(a) : null,
      unread: false,
      kind: 'ALERT_RESOLVED',
      notificationId: null,
      family: null,
      familyLabel: null,
    }))

  return [...depuisNotifs, ...depuisAlertes].sort((x, y) => y.at.localeCompare(x.at))
}

/**
 * La page d'une alerte de Sonde : relative, sous /infrastructure SEULEMENT —
 * jamais un lien que le serveur n'aurait pas posé lui-même.
 */
export function lienSonde(a: Pick<AlerteSource, 'service_key' | 'deep_link'>): string | null {
  if (a.service_key !== 'sonde' || !a.deep_link) return null
  return /^\/infrastructure(\/[A-Za-z0-9._-]+)*$/.test(a.deep_link) ? a.deep_link : null
}

/**
 * LE BADGE — ce qui demande RÉELLEMENT une attention.
 *
 * Trois notifications informatives non lues ne font pas trois urgences. Le
 * compteur de navigation ne compte donc que les alertes actives non prises en
 * charge : exactement ce qu'il comptait déjà avant la fusion, sans que la
 * lecture de l'inbox vienne le gonfler.
 */
export function attentionCount(alerts: AlerteSource[]): number {
  return alerts.filter((a) => a.state !== 'RESOLVED' && a.acknowledged_at === null).length
}

/* ─────────────────────────── regroupement du flux ───────────────────── */

export function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export function dayLabel(iso: string, nowMs: number): string {
  const now = new Date(nowMs)
  if (dayKey(iso) === dayKey(now.toISOString())) return 'Aujourd’hui'
  if (dayKey(iso) === dayKey(new Date(nowMs - 86_400_000).toISOString())) return 'Hier'
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
}

export function groupByDay(items: FeedItem[], nowMs: number): { label: string; items: FeedItem[] }[] {
  const out: { label: string; items: FeedItem[] }[] = []
  for (const it of items) {
    const label = dayLabel(it.at, nowMs)
    const last = out[out.length - 1]
    if (last && last.label === label) last.items.push(it)
    else out.push({ label, items: [it] })
  }
  return out
}

/* ───────────────────────────── filtres (§29) ────────────────────────── */

export type ActivityFilter = 'all' | 'attention' | 'unread'

export const ACTIVITY_FILTERS: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'attention', label: 'À traiter' },
  { value: 'unread', label: 'Non lu' },
]

/** Le filtre agit sur le flux ; les épinglées, elles, ne se filtrent pas —
 *  masquer un problème actif serait le contraire de ce qu'on veut. */
export function applyFilter(items: FeedItem[], f: ActivityFilter): FeedItem[] {
  if (f === 'unread') return items.filter((i) => i.unread)
  if (f === 'attention') return []
  return items
}

/* ─────────────────────── filtre par famille (08/10/2026) ─────────────────────── */

/**
 * D'OÙ VIENT LE MESSAGE — « Comptabilité · Clôtures & caisses » plutôt que
 * « Notification ». Le module est celui du type, donc de la barre latérale.
 */
export function origineDe(n: Pick<NotificationSource, 'family_label' | 'module_label'>): string {
  if (n.family_label && n.module_label) return `${n.family_label} · ${n.module_label}`
  return n.family_label ?? 'Notification'
}

export const TOUTES_FAMILLES = 'toutes'

/** L'ordre de la barre latérale — le même que le catalogue des permissions. */
const ORDRE_FAMILLES = ['accueil', 'exploitation', 'communication', 'equipe', 'comptabilite', 'securite', 'technique', 'administration']

/**
 * LES FAMILLES PRÉSENTES DANS LE FLUX, dans l'ordre de la barre latérale.
 * Le filtre n'en propose aucune autre : un bouton qui mène à une liste vide
 * n'aide personne. Les alertes résolues sans message n'ont pas de famille :
 * elles ne paraissent que sous « Toutes ».
 */
export function famillesDuFlux(items: FeedItem[]): { value: string; label: string }[] {
  const vues = new Map<string, string>()
  for (const i of items) if (i.family && !vues.has(i.family)) vues.set(i.family, i.familyLabel ?? i.family)
  const rang = (id: string): number => { const k = ORDRE_FAMILLES.indexOf(id); return k < 0 ? 99 : k }
  return [...vues.entries()]
    .sort((a, b) => rang(a[0]) - rang(b[0]) || a[1].localeCompare(b[1], 'fr'))
    .map(([value, label]) => ({ value, label }))
}

/** Le flux d'une famille ; « toutes » = le flux entier. Les épinglées ne se filtrent jamais. */
export function filtrerParFamille(items: FeedItem[], famille: string): FeedItem[] {
  if (famille === TOUTES_FAMILLES) return items
  return items.filter((i) => i.family === famille)
}
